<template>
  <div class="roles-main">
    <el-card
      shadow="never"
      class="filter-card"
    >
      <div class="search">
        <el-date-picker
          v-model="request.startDate"
          type="date"
          placeholder="Start Date"
          format="YYYY/MM/DD"
          value-format="YYYY-MM-DD"
          size="small"
          style="width: 150px"
        />
        <el-date-picker
          v-model="request.endDate"
          type="date"
          placeholder="End Date"
          format="YYYY/MM/DD"
          value-format="YYYY-MM-DD"
          size="small"
          style="width: 150px"
        />
        <el-input
          v-model="request.loginName"
          size="small"
          style="width: 200px"
          placeholder="Login Name"
          prefix-icon="el-icon-user"
          clearable
          @keyup.enter="onSearch()"
        />
        <el-button
          icon="el-icon-search"
          size="small"
          type="success"
          @click="onSearch()"
        >
          Search
        </el-button>
      </div>
    </el-card>

    <div class="overview-bar">
      <div class="ov-item">
        <span class="ov-label">Users (this page)</span>
        <span class="ov-value">{{ overviewUsers }}</span>
      </div>
      <div class="ov-item">
        <span class="ov-label">Avg First Response (this page)</span>
        <span class="ov-value">{{ overviewAvgFirst }}</span>
      </div>
      <div class="ov-item">
        <span class="ov-label">Avg Response (this page)</span>
        <span class="ov-value">{{ overviewAvgResponse }}</span>
      </div>
      <div class="ov-item ov-page">
        <span class="ov-label">Page</span>
        <span class="ov-value">{{ request.current }} / {{ page.pages || 1 }}</span>
      </div>
    </div>

    <el-table
      v-loading="loading"
      :data="page.records"
      ref="table"
      row-key="id"
      size="small"
      border
      empty-text="No data"
      highlight-current-row
      :cell-class-name="cellGrayWhite"
      class="rt-table"
    >
      <el-table-column
        prop="nickName"
        label="Name"
        min-width="160"
        align="left"
        show-overflow-tooltip
      />
      <el-table-column
        label="First Response"
        align="center"
      >
        <el-table-column
          label="Avg"
          header-align="right"
          align="right"
          min-width="130"
        >
          <template #default="scope">
            <span
              class="num-cell"
              :title="rawTitle(scope.row.avgFirstResponseSeconds)"
            >{{ formatSeconds(scope.row.avgFirstResponseSeconds) }}</span>
          </template>
        </el-table-column>
        <el-table-column
          label="Total"
          header-align="right"
          align="right"
          min-width="110"
        >
          <template #default="scope">
            <span class="num-cell">{{ scope.row.firstResponseTotalCount || 0 }}</span>
          </template>
        </el-table-column>
      </el-table-column>
      <el-table-column
        label="Response"
        align="center"
      >
        <el-table-column
          label="Avg"
          header-align="right"
          align="right"
          min-width="130"
        >
          <template #default="scope">
            <span
              class="num-cell"
              :title="rawTitle(scope.row.avgAvgResponseSeconds)"
            >{{ formatSeconds(scope.row.avgAvgResponseSeconds) }}</span>
          </template>
        </el-table-column>
        <el-table-column
          label="Total"
          header-align="right"
          align="right"
          min-width="110"
        >
          <template #default="scope">
            <span class="num-cell">{{ scope.row.avgResponseTotalCount || 0 }}</span>
          </template>
        </el-table-column>
      </el-table-column>
    </el-table>

    <el-pagination
      class="pagination"
      @current-change="changePage"
      layout="prev, pager, next, jumper"
      :page-size="request.size"
      :page-count="page.pages"
      :current-page="request.current"
    />
  </div>
</template>

<script setup>
import { computed, onMounted, reactive, ref } from '@vue/runtime-core'
import { ElMessage } from 'element-plus'
import { getUserResponseStats } from '../../../api/report'
import { convertDate } from '@/utils/util'
import { cellGrayWhite } from '@/utils/style'

const loading = ref(false)

const page = reactive({
  pages: 0,
  records: [],
})

const request = reactive({
  size: 30,
  current: 1,
  loginName: null,
  startDate: convertDate(new Date()),
  endDate: convertDate(new Date()),
})

// 把秒数格式化成友好时间:无数据 -> '—';真实 0 -> '0s';<60s 保留1位小数;>=60s 拆 h/m/s
function formatSeconds(v) {
  if (v === null || v === undefined || v === '' || Number.isNaN(Number(v))) return '—'
  const n = Number(v)
  if (n === 0) return '0s'
  if (n < 60) {
    const s = Math.round(n * 10) / 10
    return (Number.isInteger(s) ? s : s.toFixed(1)) + 's'
  }
  const total = Math.round(n)
  const h = Math.floor(total / 3600)
  const m = Math.floor((total % 3600) / 60)
  const s = total % 60
  let out = ''
  if (h > 0) out += h + 'h'
  if (m > 0) out += m + 'm'
  if (s > 0) out += s + 's'
  return out || '0s'
}

// 悬停 tooltip 显示原始秒数(无数据则不显示)
function rawTitle(v) {
  return v === null || v === undefined ? '' : v + 's'
}

// 本页聚合(跳过无效值,空页显示 '—')
function avgOf(key) {
  const vals = page.records
    .map(r => Number(r[key]))
    .filter(v => !Number.isNaN(v))
  if (vals.length === 0) return '—'
  const sum = vals.reduce((a, b) => a + b, 0)
  return formatSeconds(sum / vals.length)
}

const overviewUsers = computed(() => page.records.length)
const overviewAvgFirst = computed(() => avgOf('avgFirstResponseSeconds'))
const overviewAvgResponse = computed(() => avgOf('avgAvgResponseSeconds'))

function onSearch() {
  request.current = 1
  loadUserResponseStats()
}

function changePage(pageNum) {
  request.current = pageNum
  loadUserResponseStats()
}

async function loadUserResponseStats() {
  loading.value = true
  try {
    const { data: ret } = await getUserResponseStats(request)
    page.pages = ret.pages
    page.records = ret.records
  } catch (e) {
    ElMessage.error('Failed to load data')
  } finally {
    loading.value = false
  }
}

onMounted(async () => {
  await loadUserResponseStats()
})
</script>

<style scoped>
.filter-card {
  margin-bottom: 16px;
  border-radius: 8px;
}
.filter-card :deep(.el-card__body) {
  padding: 16px 18px;
}
.search {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  row-gap: 10px;
}
.search > *:not(h6) + * {
  margin-left: 20px;
}
.overview-bar {
  display: flex;
  flex-wrap: wrap;
  gap: 16px 48px;
  align-items: center;
  padding: 14px 20px;
  margin-bottom: 16px;
  background: var(--el-color-info-light-9, #fafafa);
  border: 1px solid var(--el-border-color-lighter, #ebeef5);
  border-radius: 8px;
}
.ov-item {
  display: flex;
  flex-direction: column;
  gap: 4px;
}
.ov-label {
  font-size: 12px;
  color: var(--el-text-color-secondary, #909399);
}
.ov-value {
  font-size: 18px;
  font-weight: 600;
  color: var(--el-text-color-primary, #303133);
}
.ov-page {
  margin-left: auto;
  text-align: right;
}
.rt-table :deep(thead th) {
  background: #f5f7fa;
  font-weight: 600;
  color: var(--el-text-color-primary, #303133);
}
.num-cell {
  font-variant-numeric: tabular-nums;
}
.pagination {
  display: flex;
  justify-content: flex-end;
  margin-top: 16px;
}
</style>
